import { useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SurfaceContext } from '@/components/agent-chat';
import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { selectSelectedNodeIds, useNodeStore } from '@/stores/node-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import type { NodeParam } from '@/types/node-types';

type WorkflowCanvasCtx = Extract<
  SurfaceContext,
  { surface: 'workflow_canvas' }
>;

/**
 * The gateway drops a surface context over 8 KB rather than truncating it, and
 * says so only in its own logs — so an oversize snapshot costs the agent every
 * bit of grounding instead of the excess. Stay under that ceiling with room to
 * spare.
 */
const MAX_SURFACE_CONTEXT_BYTES = 7 * 1024;

/** How much of a param value is worth sending: enough to recognize it. */
const MAX_PARAM_VALUE_CHARS = 240;
/** The focused node's prompts are what the user asks to edit, so keep more. */
const MAX_FOCUSED_VALUE_CHARS = 1500;

function clip(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…[truncated]`;
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/**
 * Keep the most leading nodes (and the edges among them) that fit. A graph
 * missing some nodes still grounds the agent; one the gateway drops outright
 * grounds it in nothing. `omitted_node_count` records how many nodes were
 * dropped, so a partial snapshot is distinguishable from the whole canvas.
 */
function trimGraphToFit(ctx: WorkflowCanvasCtx): WorkflowCanvasCtx {
  const nodes = ctx.nodes ?? [];
  const edges = ctx.edges ?? [];

  const withFirst = (keep: number): WorkflowCanvasCtx => {
    const keptNodes = nodes.slice(0, keep);
    const keptIds = new Set(keptNodes.map((n) => n.id));
    const omitted = nodes.length - keep;
    return {
      ...ctx,
      nodes: keep > 0 ? keptNodes : undefined,
      edges:
        keep > 0
          ? edges.filter((e) => keptIds.has(e.source) && keptIds.has(e.target))
          : undefined,
      ...(omitted > 0 ? { omitted_node_count: omitted } : {}),
    };
  };

  // Size grows with the node count, so binary-search the largest count that
  // fits. Zero always fits: that's the rest of the context without a graph.
  let lo = 0;
  let hi = nodes.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (byteLength(withFirst(mid)) <= MAX_SURFACE_CONTEXT_BYTES) lo = mid;
    else hi = mid - 1;
  }
  return withFirst(lo);
}

/**
 * Shed detail until the context fits, giving up the least useful part first.
 *
 * The graph's shape is the last thing to go: it is what the agent needs to
 * reason about wiring and to run what is on screen. Node prompts, the type
 * catalog, and the node-type allowlist are conveniences it can ask for or
 * fall back to looser backend validation without.
 */
function fitToBudget(ctx: WorkflowCanvasCtx): WorkflowCanvasCtx {
  if (byteLength(ctx) <= MAX_SURFACE_CONTEXT_BYTES) return ctx;

  const withoutCatalog = { ...ctx, catalog: undefined };
  if (byteLength(withoutCatalog) <= MAX_SURFACE_CONTEXT_BYTES) {
    return withoutCatalog;
  }

  const withoutFocusedConfig = { ...withoutCatalog, focused_config: undefined };
  if (byteLength(withoutFocusedConfig) <= MAX_SURFACE_CONTEXT_BYTES) {
    return withoutFocusedConfig;
  }

  // A missing allowlist just means the backend validates add-node proposals
  // less strictly; a trimmed graph means the agent can't reason about the
  // canvas at all. An org's catalog size isn't something this hook
  // controls, so drop it before touching the graph.
  const withoutAllowlist = {
    ...withoutFocusedConfig,
    available_node_types: undefined,
  };
  if (byteLength(withoutAllowlist) <= MAX_SURFACE_CONTEXT_BYTES) {
    return withoutAllowlist;
  }

  const withoutParams = {
    ...withoutAllowlist,
    nodes: withoutAllowlist.nodes?.map((n) => ({
      ...n,
      params: undefined,
    })),
  };
  if (byteLength(withoutParams) <= MAX_SURFACE_CONTEXT_BYTES) {
    return withoutParams;
  }

  return trimGraphToFit(withoutParams);
}

/** Serialize non-empty, visible params into the compact wire format. */
function serializeNodeParams(
  params: NodeParam[] | undefined,
): WorkflowCanvasCtx['nodes'] extends Array<infer N>
  ? NonNullable<(N & { params?: unknown })['params']> | undefined
  : undefined {
  if (!params?.length) return undefined as never;
  const result = params
    .filter(
      (p) =>
        !p.hidden &&
        p.value !== null &&
        p.value !== undefined &&
        p.value !== '',
    )
    .map((p) => ({
      field_id: p.field_id,
      display_name: p.display_name,
      value: clip(String(p.value), MAX_PARAM_VALUE_CHARS),
      ...(p.options?.length ? { options: p.options } : {}),
    }));
  return (result.length > 0 ? result : undefined) as never;
}

/** Build the rich focused-node config block (Wire 1.0.27). */
function buildFocusedConfig(
  params: NodeParam[] | undefined,
): WorkflowCanvasCtx['focused_config'] | undefined {
  if (!params?.length) return undefined;

  let systemPrompt: string | undefined;
  let task: string | undefined;
  let nodeModel: string | undefined;
  const extraParams: Record<string, string> = {};
  const availableFields: string[] = [];

  for (const p of params) {
    if (p.hidden || p.plugged) continue;
    availableFields.push(p.field_id);
    const val =
      p.value != null && p.value !== ''
        ? clip(String(p.value), MAX_FOCUSED_VALUE_CHARS)
        : undefined;
    if (p.field_id === 'system_prompt') {
      systemPrompt = val;
    } else if (
      p.field_id === 'task' ||
      p.field_id === 'user_prompt' ||
      p.field_id === 'request'
    ) {
      task = val;
    } else if (p.field_id === 'model' || p.field_id === 'node_model') {
      nodeModel = val;
    } else if (val) {
      extraParams[p.display_name] = val;
    }
  }

  if (
    !systemPrompt &&
    !task &&
    !nodeModel &&
    Object.keys(extraParams).length === 0
  ) {
    return undefined;
  }

  return {
    ...(systemPrompt ? { system_prompt: systemPrompt } : {}),
    ...(task ? { task } : {}),
    ...(nodeModel ? { node_model: nodeModel } : {}),
    ...(Object.keys(extraParams).length > 0
      ? { extra_params: extraParams }
      : {}),
    available_fields: availableFields,
  };
}

/**
 * What the agent should know about the workflow the user is looking at: the
 * live graph, the focused node's configuration, and the node types in play.
 *
 * The canvas is a single global store, so this reads the same state wherever
 * the editor is mounted — inside its own page, or beside a chat rail in a
 * surface pane. A chat that sent only the workflow id would leave the agent
 * blind to unsaved edits and unable to run the graph on screen.
 *
 * Returns `null` for an unsaved canvas with nothing on it — there is nothing
 * to ground against yet.
 */
export function useWorkflowCanvasSurfaceContext(
  workflowId: string | null | undefined,
  // The canvas padlock is session-only local state, not part of the
  // persisted workflow -- the caller (whichever component actually knows
  // the mounted canvas's lock state) passes it in. Without it the agent
  // would see is_locked: false and keep proposing edits Apply then refuses,
  // even though the legacy in-editor chat already sends the effective lock.
  isCanvasLocked = false,
): SurfaceContext | null {
  const persistedLocked = useWorkflowStore(
    (s) => s.currentWorkflow?.is_locked ?? false,
  );
  const isLocked = persistedLocked || isCanvasLocked;
  const isLoadingWorkflow = useWorkflowStore((s) => s.isLoadingWorkflow);
  const loadedWorkflowId = useWorkflowStore((s) => s.currentWorkflow?.id);

  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  // Several selected nodes are not one focus: naming the first would scope
  // the agent (and the chat's marker) to a node the user did not single out.
  const selectedNodeId =
    selectedNodeIds.length === 1 ? (selectedNodeIds[0] ?? null) : null;

  const focusedNode = useNodeStore(
    useShallow((state) => {
      if (!selectedNodeId) return null;
      const node = state.nodes.find((n) => n.id === selectedNodeId);
      if (!node) return null;
      const struct = node.data?.struct;
      return {
        node_id: selectedNodeId,
        node_display_name: getStructDisplayLabel(struct) ?? selectedNodeId,
        node_type: (struct?.node_id as string | undefined) ?? '',
        _params: struct?.params as NodeParam[] | undefined,
      };
    }),
  );

  // Compact node/edge snapshot for agent grounding (Wire 1.0.26/1.0.27).
  // Select raw arrays with useShallow so refs are stable when nothing changed;
  // transform in useMemo to avoid creating new objects on every render.
  const rawNodes = useNodeStore(useShallow((state) => state.nodes ?? []));
  const rawEdges = useNodeStore(useShallow((state) => state.edges ?? []));
  const groupedNodes = useNodeStore(useShallow((state) => state.groupedNodes));

  const canvasNodes = useMemo(
    () =>
      rawNodes.map((n) => ({
        id: n.id,
        label: getStructDisplayLabel(n.data?.struct) ?? undefined,
        node_type: (n.data?.struct?.node_id as string | undefined) ?? undefined,
        params: serializeNodeParams(
          n.data?.struct?.params as NodeParam[] | undefined,
        ),
      })),
    [rawNodes],
  );

  const canvasEdges = useMemo(
    () =>
      // Edge ids and port handles are long generated strings the backend
      // never reads; sending them pushed mid-size graphs over budget. The
      // field_id the agent reasons about is target_param_id, not the handle.
      rawEdges.map((e) => ({
        source: e.source,
        target: e.target,
        ...(e.target_param_id ? { target_param: e.target_param_id } : {}),
      })),
    [rawEdges],
  );

  // Catalog entries for node types already on the canvas (Wire 1.0.27).
  // Only types present on the canvas are sent — not the full registry.
  const catalog = useMemo(() => {
    const typesOnCanvas = new Set(
      rawNodes
        .map((n) => n.data?.struct?.node_id as string | undefined)
        .filter((t): t is string => !!t),
    );
    const allStructs = Object.values(groupedNodes ?? {}).flat();
    const entries = Array.from(typesOnCanvas)
      .map((nodeType) => {
        const s = allStructs.find((c) => c.node_id === nodeType);
        if (!s) return null;
        return {
          node_type: nodeType,
          display_name: s.display_name,
          params: (s.params ?? []).map((p) => ({
            field_id: p.field_id,
            display_name: p.display_name,
            ...(p.options?.length ? { options: p.options } : {}),
          })),
        };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);
    return entries.length > 0 ? entries : undefined;
  }, [rawNodes, groupedNodes]);

  const availableNodeTypes = useMemo(
    () => [
      ...new Set(
        Object.values(groupedNodes ?? {})
          .flat()
          .map((node) => node.node_id)
          .filter((nodeType): nodeType is string => !!nodeType),
      ),
    ],
    [groupedNodes],
  );

  // A canvas that was 'new' when a save completes gets a real store id
  // before the route/pane it lives in has navigated to match -- the save
  // flow confirms the new row exists before it redirects, which can take a
  // while. Once this hook has actually observed the store settle on 'new'
  // for the current 'new' request, a later divergence to a real id is that
  // same canvas after its first save, not a switch to leave grounding
  // against -- track it explicitly rather than guess from id shapes alone,
  // which can't otherwise tell that apart from stale leftovers after a
  // genuine switch (round-tripping through 'new' looks identical either way).
  const prevRequestedWorkflowIdRef = useRef<string | null | undefined>(
    undefined,
  );
  const hasConfirmedNewCanvasRef = useRef(false);
  if (prevRequestedWorkflowIdRef.current !== workflowId) {
    hasConfirmedNewCanvasRef.current = false;
    prevRequestedWorkflowIdRef.current = workflowId;
  }
  if (workflowId === 'new' && loadedWorkflowId === 'new') {
    hasConfirmedNewCanvasRef.current = true;
  }

  // The node/workflow stores are global and outlive any one workflow, so a
  // pane that just switched to `workflowId` -- saved or a fresh 'new' canvas
  // -- can render a frame or two against the previous workflow's nodes (or
  // the loading placeholder) before the store catches up. Grounding the
  // agent against that leftover graph is worse than grounding it against
  // nothing, so every other case waits for the store to confirm it caught up.
  const isRequestedWorkflowLoaded =
    !isLoadingWorkflow &&
    (loadedWorkflowId === workflowId ||
      (workflowId === 'new' && hasConfirmedNewCanvasRef.current));

  // The authoritative id once loaded: for the just-saved 'new' canvas this
  // is the real id the store already has, ahead of the stale 'new' request.
  const isSavedWorkflow = !!loadedWorkflowId && loadedWorkflowId !== 'new';

  return useMemo<SurfaceContext | null>(() => {
    if (!isRequestedWorkflowLoaded) return null;
    // Require at least some canvas context (nodes or a saved workflow_id).
    if (!isSavedWorkflow && canvasNodes.length === 0) return null;
    const focusedConfig = focusedNode
      ? buildFocusedConfig(focusedNode._params)
      : undefined;
    return fitToBudget({
      surface: 'workflow_canvas',
      ...(isSavedWorkflow ? { workflow_id: loadedWorkflowId! } : {}),
      ...(focusedNode
        ? {
            focused_node: {
              node_id: focusedNode.node_id,
              node_display_name: focusedNode.node_display_name,
              node_type: focusedNode.node_type,
            },
          }
        : {}),
      ...(focusedConfig ? { focused_config: focusedConfig } : {}),
      ...(canvasNodes.length > 0 ? { nodes: canvasNodes } : {}),
      ...(canvasEdges.length > 0 ? { edges: canvasEdges } : {}),
      is_locked: isLocked,
      ...(availableNodeTypes.length > 0
        ? { available_node_types: availableNodeTypes }
        : {}),
      ...(catalog ? { catalog } : {}),
    });
  }, [
    isRequestedWorkflowLoaded,
    loadedWorkflowId,
    isSavedWorkflow,
    focusedNode,
    canvasNodes,
    canvasEdges,
    isLocked,
    availableNodeTypes,
    catalog,
  ]);
}
